import React, { useEffect, useMemo, useState } from 'react'
import { useBoardsContext } from '../context/BoardsContext'
import useKanbanChannel from '../hooks/useKanbanChannel'
import useBoardMutations from '../hooks/useBoardMutations'
import useBoardFacets from '../hooks/useBoardFacets'
import { DEFAULT_STATUS_PRESET, DEFAULT_TAG_PRESET } from '../constants/colorPresets'
import BoardHeader from './BoardHeader'
import BoardContent from './BoardContent'
import ColorPickerPopover from './ColorPickerPopover'

const FILTER_VISIBLE_LIMIT = 12

function BoardsPage() {
  const { selectedBoardId, currentBoard, boardDetailQuery, searchQuery, setSearchQuery, canEdit, getLabelColor, boardSearch } = useBoardsContext()
  const { updateBoard } = useBoardMutations(selectedBoardId)
  const [colorPickerTarget, setColorPickerTarget] = useState(null)
  const [showAllStatuses, setShowAllStatuses] = useState(false)
  const [showAllTags, setShowAllTags] = useState(false)
  const [showRareTags, setShowRareTags] = useState(false)

  const facetsQuery = useBoardFacets(selectedBoardId, boardSearch.debouncedQuery)

  const { statuses, commonTags, rareTags } = useMemo(() => {
    const statusCounts = facetsQuery.data?.statuses || {}
    const tagCounts = facetsQuery.data?.tags || {}

    const allTags = Object.entries(tagCounts).sort((a, b) => b[1] - a[1])
    return {
      statuses: Object.entries(statusCounts).sort((a, b) => b[1] - a[1]),
      commonTags: allTags.filter(([, count]) => count >= 2),
      rareTags: allTags.filter(([, count]) => count < 2)
    }
  }, [facetsQuery.data])

  useKanbanChannel(selectedBoardId)

  useEffect(() => {
    const titleEl = document.querySelector('main h1')
    if (titleEl) {
      titleEl.textContent = currentBoard ? currentBoard.name : 'Boards'
    }
    return () => {
      if (titleEl) titleEl.textContent = 'Boards'
    }
  }, [currentBoard?.name])

  const handleColorSelect = (type, name, presetKey) => {
    const newColors = {
      ...currentBoard.label_colors,
      [type]: {
        ...(currentBoard.label_colors?.[type] || {}),
        [name]: presetKey
      }
    }
    updateBoard.mutate({ label_colors: newColors })
    setColorPickerTarget(null)
  }

  const currentPresetFor = (type, name) => {
    return currentBoard?.label_colors?.[type]?.[name] || (type === 'statuses' ? DEFAULT_STATUS_PRESET : DEFAULT_TAG_PRESET)
  }

  const renderPill = (type, name, count) => {
    const colors = getLabelColor(type, name)
    const isActive = searchQuery.includes(name)
    const pickerOpen = colorPickerTarget?.type === type && colorPickerTarget?.name === name

    return (
      <div key={`${type}-${name}`} style={{ position: 'relative' }}>
        <button
          className={`kb-filter-pill${isActive ? ' kb-filter-pill--active' : ''}`}
          style={{ backgroundColor: colors.bg, color: colors.text, borderColor: colors.border }}
          onClick={() => setSearchQuery(prev => {
            if (prev.includes(name)) return prev.replace(name, '').replace(/\s{2,}/g, ' ').trim()
            return prev ? `${prev} ${name}` : name
          })}
        >
          {name} <span className="kb-filter-pill-count">{count}</span>
          {canEdit && (
            <span
              className="kb-filter-pill-color-trigger"
              style={{ backgroundColor: colors.text }}
              onClick={(e) => {
                e.stopPropagation()
                setColorPickerTarget(pickerOpen ? null : { type, name })
              }}
            />
          )}
        </button>
        {pickerOpen && (
          <ColorPickerPopover
            currentPreset={currentPresetFor(type, name)}
            onSelect={(presetKey) => handleColorSelect(type, name, presetKey)}
            onClose={() => setColorPickerTarget(null)}
          />
        )}
      </div>
    )
  }

  return (
    <div>
      <BoardHeader />

      {selectedBoardId && currentBoard && (
        <>
          <div className="mb-4">
            <div className="rounded-lg bg-white shadow-md">
              <div className="w-full px-3">
                <div className="flex items-center py-2 px-4">
                  <input
                    type="text"
                    className="appearance-none flex-1 text-gray-700 py-2 text-lg focus:outline-none bg-transparent border-none"
                    style={{ minWidth: 0, width: searchQuery ? `${searchQuery.length + 1}ch` : '100%' }}
                    placeholder="Search cards..."
                    autoComplete="off"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {boardSearch.isActive && (
                    <span className="text-xs text-gray-500 ml-3 flex-shrink-0">
                      {boardSearch.isPending
                        ? 'searching…'
                        : `${boardSearch.totalCount} match${boardSearch.totalCount === 1 ? '' : 'es'}`}
                    </span>
                  )}
                  {searchQuery && (
                    <button
                      className="text-gray-400 hover:text-gray-600 text-xl leading-none ml-2 flex-shrink-0"
                      style={{ outline: 'none', boxShadow: 'none' }}
                      onClick={() => setSearchQuery('')}
                    >
                      &times;
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          {(statuses.length > 0 || commonTags.length > 0 || rareTags.length > 0) && (
            <div className="kb-filter-bar">
              {statuses.length > 0 && (
                <div className="kb-filter-row">
                  <span className="kb-filter-row-label">Status:</span>
                  {(showAllStatuses ? statuses : statuses.slice(0, FILTER_VISIBLE_LIMIT))
                    .map(([name, count]) => renderPill('statuses', name, count))}
                  {statuses.length > FILTER_VISIBLE_LIMIT && (
                    <button className="kb-filter-more" onClick={() => setShowAllStatuses(s => !s)}>
                      {showAllStatuses ? 'show less' : `+ ${statuses.length - FILTER_VISIBLE_LIMIT} more`}
                    </button>
                  )}
                </div>
              )}
              {(commonTags.length > 0 || rareTags.length > 0) && (
                <div className="kb-filter-row">
                  <span className="kb-filter-row-label">Tags:</span>
                  {(showAllTags ? commonTags : commonTags.slice(0, FILTER_VISIBLE_LIMIT))
                    .map(([name, count]) => renderPill('tags', name, count))}
                  {commonTags.length > FILTER_VISIBLE_LIMIT && (
                    <button className="kb-filter-more" onClick={() => setShowAllTags(s => !s)}>
                      {showAllTags ? 'show less' : `+ ${commonTags.length - FILTER_VISIBLE_LIMIT} more`}
                    </button>
                  )}
                  {showRareTags && rareTags.map(([name, count]) => renderPill('tags', name, count))}
                  {rareTags.length > 0 && (
                    <button className="kb-filter-more" onClick={() => setShowRareTags(s => !s)}>
                      {showRareTags ? 'hide rare' : `+ ${rareTags.length} rare`}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          <BoardContent />
        </>
      )}

      {selectedBoardId && !currentBoard && boardDetailQuery.isLoading && (
        <div className="flex justify-center py-12 text-gray-400">
          Loading...
        </div>
      )}

      {!selectedBoardId && (
        <div className="text-center py-12 text-gray-500">
          <a href="/kanban_boards" className="link">Go to All Boards</a> to select or create a board.
        </div>
      )}
    </div>
  )
}

export default BoardsPage
